import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, map} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {MsgContent} from '../MessageContent/msg-content';
import {TypedJSON} from 'typedjson';
import {WidgetBaseComponent} from '../DoraWidget/widget-base/widget-base.component';
import {SingleParameter} from '../MessageContent/single-parameter';
import {MultipleParameter} from '../MessageContent/multiple-parameter';
import {IConfigurationCaller} from './iconfiguration-caller';
import {forEach} from '@angular/router/src/utils/collection';
import {SrcParameter} from './src-parameter';
import {PublisherService} from '../pusblisher/publisher.service';
import {IPublisher} from '../pusblisher/ipublisher';
import {PublisherDataManagement} from '../pusblisher/DataManagement/publisher-data-management';
import {ComponentType} from './component-type.enum';
import {PublisherDoraServer} from '../pusblisher/DoraServer/publisher-dora-server';

declare function extGetAppConfigurationPath(): string;

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  private parametersPromise: Promise<MsgContent>;

  // @ts-ignore
  constructor(private http: HttpClient) {
    this.read();
    console.log('Configuration service: finito costruttore');
  }

  private read() {

    this.parametersPromise = this.http.get(extGetAppConfigurationPath() + '/AppConfiguration.json').pipe(
      map( (data: string) => {
        let msgContent: MsgContent;
        // tslint:disable-next-line:prefer-const
        let serialized = new TypedJSON(MsgContent);

        msgContent = serialized.parse(data);

        console.log('Received configuration');
        return msgContent;
      })
    ) .toPromise();


    console.log('Configuration service: finito read');
  }

  public loadPublisher(caller: PublisherService): void {
    this.parametersPromise.then((parameters) => {
      const publishersConfig: MultipleParameter = parameters.getMultipleParameter('Publishers');

      if (publishersConfig === undefined) {
        console.error('Publisher section not present in AppConfiguration');
        return;
      }
      const publishers: IPublisher[] = [];

      publishersConfig.multipleParameters.forEach( publisherConfig => {
        switch (publisherConfig.name) {
          case 'DataManager':
            // get progressive
            try {
              const newPublisher = new PublisherDataManagement(this.http, publisherConfig.getSingleParameter('Progressive').value);
              publishers.push(newPublisher);
            } catch (e) {
              console.error('Progressive not found in publisher ' + publishersConfig.name);
            }
            break;

          case 'DoraServer':
            // get progressive
            try {
              const newPublisher = new PublisherDoraServer(this.http, publisherConfig.getSingleParameter('Progressive').value);
              publishers.push(newPublisher);
            } catch (e) {
              console.error('Progressive not found in publisher ' + publishersConfig.name);
            }
            break;

          default:
              console.error('Unable to handle Publisher ' + publisherConfig.name);
        }
      });

      // finished: signal to caller result
      caller.onPublisherLoaded(publishers);
    });
  }
  /**
   * Inject parameters from json configuration to widget
   * @param src Widget to inject
   */
  public injectParametersToComponent(src: IConfigurationCaller, attributes: NamedNodeMap = null) {
    this.parametersPromise.then((parameters) => {

        // set progressive from HTML
      this.setProgressive(src, attributes);

      const mySrcParameters: SrcParameter[] = this.getParameterFromSource(src);

      // loop for all decorated attributes
      const widgetConfiguration: MultipleParameter = this.getComponentConfiguration(src, parameters);

      // Inject parameters from external configuration
      if (widgetConfiguration && widgetConfiguration !== null) {
        // Single parameters
        // @ts-ignore
        for (const singleParameter: SingleParameter of widgetConfiguration.singleParameters) {
          if (singleParameter.Name !== 'CallerName') {
            const srcParameter = mySrcParameters.find((data) => data.name === singleParameter.Name);
            if (srcParameter !== undefined ) {
              if (!srcParameter.isMultiple()) {
                src[singleParameter.Name] = singleParameter.getValue();
              }
            } else {
              console.log('Wigdet [' + src.CallerName + '], parameter [' + singleParameter.Name +
                '] found in configuration, but not present in attribute list');
            }
          }
        }

        // multiple parameters
        // loop for source parameter
        mySrcParameters.forEach(srcParameter => {
          if (!srcParameter.isMultiple()) {
            return;
          }
          // @ts-ignore
          for (const multipleParameter: MultipleParameter of widgetConfiguration.getMultipleParameters(srcParameter.name)) {
            const newParam = srcParameter.createChild();
            // loop for single parameter
            multipleParameter.singleParameters.forEach(singleParameter => {
              const res = Reflect.getMetadata('design:type', newParam, singleParameter.Name);
              if (res !== undefined && res.name !== 'Array') {
                this.setAttributeValue(newParam, singleParameter.name, singleParameter.value);
                return;
              }
            });

          }

          src[srcParameter.name] = srcParameter.children;
        });
    }

      // Inject parameters from HTML attributes.
      // Attributes are case insensitive
      if (attributes !== null) {
        for (let i = 0; i < attributes.length; i++) {
          const attribute = attributes.item(i);

          const srcAttributeName = mySrcParameters.find((data) => data.name.toLowerCase() === attribute.name.toLowerCase());
          if (srcAttributeName !== undefined) {
              if (srcAttributeName.isMultiple()) {
                  this.createMultipleAttributeFromHTML(srcAttributeName, attribute.value);
                  src[srcAttributeName.name] = srcAttributeName.children;
              } else {
                  this.setAttributeValue(src, srcAttributeName.name, attribute.value);
              }
          } else {
            console.log('Wigdet [' + src.CallerName + '], parameter [' + attribute.name +
              '] found in HTML, but not present in attribute list');
          }
        }
      }

      // All parameters are injected
      src.onParameterInjectedFinished();
    });
  }

  private createMultipleAttributeFromHTML(srcParameter: SrcParameter, value: string): void {
    // check if value is empty
    if (value === null || value === undefined || value === '') {
        return;
    }

    const parametersStr = value.split('§');

    // get parameter from child
      // tslint:disable-next-line:new-parens
    const child = new (srcParameter.constructionObject);

    const elementParameters = this.getParameterFromSource(child);

    // check number of parameters
    if (parametersStr.length % elementParameters.length !== 0) {
        console.error('Parameter ' + srcParameter.name + ' must contain ' + parametersStr.length + ' values or a multiple');
        return;
    }

    const n = parametersStr.length / elementParameters.length;

    // empty
    srcParameter.children = [];

    let valueCounter = 0;
    for (let i = 0; i < n; i++) {
        const newElement = srcParameter.createChild();
        elementParameters.forEach(srcParameterChild => {
            this.setAttributeValue(newElement, srcParameterChild.name, parametersStr[valueCounter]);
            valueCounter++;
        });
    }
  }

  /**
   * Get all decorated attributes from source
   * @param src Source object containing docorated attribute
   */
  private getParameterFromSource(src: any): SrcParameter[] {
    const srcParameters: SrcParameter[] = [];

    Object.getOwnPropertyNames(src).forEach(paramName => {

      // Single parameter
      let res = Reflect.getMetadata('design:type', src, paramName);
      if (res !== undefined && res.name !== 'Array') {
        // tslint:disable-next-line:new-parens
        srcParameters.push(new SrcParameter(paramName));
        return;
      }

      // Multiple parameter
      res = Reflect.getMetadata('DoraPropertyArray', src, paramName);
      if (res !== undefined) {
        const srcParameter = new SrcParameter(paramName);
        srcParameter.setMultiple();
        srcParameter.constructionObject = res;
        srcParameters.push(srcParameter);
      }
    });

    return srcParameters;
  }

  private setAttributeValue(src: any, attributeName: string, attributeValue: string): void {
    if (attributeName !== null && attributeName !== undefined) {
      switch (typeof src[attributeName]) {
        case 'number':
          src[attributeName] = Number(attributeValue);
          break;
        case 'boolean':
          src[attributeName] = attributeValue.toLowerCase() === 'true' ? true : false;
          break;
        case 'string':
          src[attributeName] = attributeValue;
          break;
        default:
          console.log('Attribute type not handled: ' + typeof src[attributeName] + ' Attribute: ' + attributeName);
      }
    }
  }

  /**
   * Read Component configuration widget or publisher
   * @param src widget component
   * @param compType component type eg. Widgets, Publishers
   * @param parameters complete parameters
   */
  private getComponentConfiguration(src: IConfigurationCaller, parameters: MsgContent): MultipleParameter {
    return parameters.getMultipleParameter(ComponentType[src.CallerType]).multipleParameters.find( (x) => {
      if (x.name === src.CallerSubType) {
        const progressiveParameter: SingleParameter = x.getSingleParameter('Progressive');
        if (progressiveParameter != null && progressiveParameter.getValueString() === src.CallerProgressive) {
          return true;
        }
      }
      return false;
    });
  }

    private setProgressive(src: IConfigurationCaller, attributes: NamedNodeMap) {
        if (attributes !== null) {
            for (let i = 0; i < attributes.length; i++) {
                const attribute = attributes.item(i);
                if (attribute.name.toLowerCase() === 'progressive') {
                    src.setCallerProgressive(attribute.value);
                }
            }
        }
    }
}

